import { ShareIcon } from '@heroicons/react/24/outline'
import isEmpty from 'lodash/isEmpty'
import isUndefined from 'lodash/isUndefined'
import { useCallback, useEffect, useMemo } from 'react'
import { useCopyUrlShare } from 'shared/hooks/useCopyUrlShare'

import Sharebox from '../Sharebox'
import ShareboxDialog from '../Sharebox/ShareboxDialog'

interface IKlassInfoHeaderProps {
  isMobile: boolean
  klassId: string
  title: string
  panelText: string | null
  shareUrl: {
    [shareType: string]: string
  } | null
  imgUrl: string
}

const KlassInfoHeader = ({
  isMobile,
  klassId,
  title,
  panelText,
  shareUrl,
  imgUrl,
}: IKlassInfoHeaderProps) => {
  const { copyUrlShare, isDialogOpened, onDialogOpenHandler, onDialogCloseHandler } =
    useCopyUrlShare()

  const defaultUrl = useMemo(
    () =>
      klassId?.includes('kdc')
        ? `https://likelion.net/onboarding/${klassId}`
        : `https://likelion.net/school/${klassId}`,
    [klassId],
  )

  const shareList = [
    {
      id: 'kakao',
      imgSrc: '/svg/icon_share_kakao.svg',
      title: '카카오톡',
      installTalk: true,
      onShareHandler: useCallback(() => {
        if (shareUrl && !isUndefined(shareUrl.kakao)) {
          window.Kakao.Share.sendDefault({
            objectType: 'feed',
            content: {
              title: title,
              description: panelText,
              imageUrl: imgUrl,
              link: {
                mobileWebUrl: shareUrl ? shareUrl.kakao : defaultUrl,
                webUrl: shareUrl ? shareUrl.kakao : defaultUrl,
              },
            },
            buttons: [
              {
                title: '멋사에서 보기',
                link: {
                  mobileWebUrl: shareUrl ? shareUrl.kakao : defaultUrl,
                  webUrl: shareUrl ? shareUrl.kakao : defaultUrl,
                },
              },
            ],
          })
        }
      }, [imgUrl, panelText, shareUrl, title]),
    },
    {
      id: 'url',
      imgSrc: '/svg/icon_share_url.svg',
      title: 'URL복사',
      onShareHandler: useCallback(
        () => copyUrlShare(shareUrl ? shareUrl.url : defaultUrl),
        [copyUrlShare, defaultUrl, shareUrl],
      ),
    },
  ]

  const shareListFilter = useMemo(
    () =>
      klassId?.includes('kdc') || klassId?.includes('kdt')
        ? shareList
        : shareList.filter(item => item.id !== 'kakao'),
    [klassId],
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { Kakao } = window
      if (!Kakao.isInitialized()) {
        Kakao.init(process.env.NEXT_PUBLIC_KAKAO_SHARE)
      }
    }
  }, [])

  return (
    <>
      <header
        className={`flex items-start justify-between ${isMobile ? '' : 'relative'} ${
          panelText ? '' : 'mb-[18px] border-b border-gray-300'
        }`}
      >
        <h3
          className={`break-keep text-xl font-semibold ${panelText ? '' : 'mb-3'}`}
          style={{ lineHeight: isMobile ? 1.3 : 1.375 }}
        >
          {title}
        </h3>
        {!isEmpty(shareUrl) && (
          <div>
            <ShareIcon
              className="ml-2.5 h-auto w-6 shrink-0 cursor-pointer"
              onClick={() => {
                onDialogOpenHandler()
              }}
            />
            {!isMobile && isDialogOpened && (
              <Sharebox shareList={shareListFilter} onClose={onDialogCloseHandler} />
            )}
          </div>
        )}
      </header>
      {panelText && !klassId.includes('kdc') && (
        <p className="mb-[18px] break-keep border-b border-gray-300 py-3">
          <span className="text-sm text-gray-500">{panelText}</span>
        </p>
      )}
      {isMobile && (
        <ShareboxDialog
          isOpen={isDialogOpened}
          onClose={onDialogCloseHandler}
          shareList={shareListFilter}
        />
      )}
    </>
  )
}

export default KlassInfoHeader
